/**
 * Repair history and the review hold (shelf-board-assembly plan D5, decided: held for review).
 *
 * A family that adjusts a fitted layout records three snapshots of its parameters: as read from
 * the photo (`raw`), after normalisation (snapping, defaults, dropped values) and after repair
 * (supports added, parts left out, posts moved), with typed changes between them and the parts the
 * repair added or removed. The history travels in the design's `fitHistory` parameter and becomes
 * the v2 contract's `history` (src/families/contract.ts).
 *
 * A repair that changes the photographed design a lot is held for review: the user sees what
 * changed and accepts it (or edits the design) before anything is exported. The bounds are a
 * versioned policy, tuned on development data (Phase 2).
 */
import type { AssemblyPart } from './assembly';
import { HistorySchema, type History, type HistoryChange } from './contract';

export const REPAIR_HOLD_POLICY = {
  id: 'repair-hold',
  version: '1',
  /** More added parts than this is held: one support under one free end is ordinary. */
  maxAddedParts: 1,
  /** Any part the repair leaves out is held: the photo showed it. */
  maxRemovedParts: 0,
  /** A shelf end or post moved further than this fraction of the overall width is held. */
  maxMoveOfWidth: 0.05,
} as const;

export interface RepairHold {
  held: boolean;
  reasons: string[];
  policy: string;
  addedParts: string[];
  removedParts: string[];
  maxMoveOfWidth: number;
  /** Readable names of the added and removed parts (removed parts are no longer in the build to look up). */
  labels: Record<string, string>;
  /** Structure the photo showed that normalisation dropped before any repair (a divider too near an edge, tiers past the limit …). */
  normalised: string[];
}

const near = (a: unknown, b: unknown) => (typeof a === 'number' && typeof b === 'number' ? Math.abs(a - b) <= 1e-6 : JSON.stringify(a) === JSON.stringify(b));

/** Typed changes between two flat-ish parameter objects, one level into arrays of objects. */
export function diffParams(from: Record<string, unknown>, to: Record<string, unknown>, by: HistoryChange['by'], keys: readonly string[], notes: Partial<Record<string, string>> = {}): HistoryChange[] {
  const out: HistoryChange[] = [];
  for (const key of keys) {
    const a = from[key], b = to[key];
    if (near(a, b)) continue;
    if (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((x) => x && typeof x === 'object' && !Array.isArray(x))) {
      a.forEach((x, i) => {
        const y = b[i] as Record<string, unknown>;
        for (const k of new Set([...Object.keys(x as object), ...Object.keys(y)])) {
          const va = (x as Record<string, unknown>)[k], vb = y[k];
          if (near(va, vb)) continue;
          const op = va === false && vb === true ? 'add' : va === true && vb === false ? 'remove' : 'modify';
          out.push({ op, target: `${key}[${i}].${k}`, by, from: va, to: vb, note: notes[key] ?? `${key} ${i + 1}: ${k} ${op === 'add' ? 'added' : op === 'remove' ? 'removed' : 'changed'}` });
        }
      });
      continue;
    }
    const op = (a === undefined || a === null) ? 'add' : (b === undefined || b === null) ? 'remove' : 'modify';
    out.push({ op, target: key, by, from: a ?? null, to: b ?? null, note: notes[key] ?? `${key} ${op === 'add' ? 'added' : op === 'remove' ? 'dropped' : 'changed'}` });
  }
  return out;
}

/** How far any horizontal board end moved, as a fraction of the overall width. */
export function maxHorizontalMove(a: readonly { xStart: number; xEnd: number }[], b: readonly { xStart: number; xEnd: number }[]): number {
  if (a.length !== b.length) return 1;
  return a.reduce((m, h, i) => Math.max(m, Math.abs(h.xStart - b[i]!.xStart), Math.abs(h.xEnd - b[i]!.xEnd)), 0);
}

/** Whether a repair exceeds the hold policy, and why. */
export function assessRepair(before: readonly Pick<AssemblyPart, 'id' | 'label'>[], after: readonly Pick<AssemblyPart, 'id' | 'label'>[], maxMoveOfWidth: number, normalised: readonly string[] = []): RepairHold {
  const was = new Set(before.map((p) => p.id)), now = new Set(after.map((p) => p.id));
  const addedParts = [...now].filter((id) => !was.has(id)).sort();
  const removedParts = [...was].filter((id) => !now.has(id)).sort();
  const P = REPAIR_HOLD_POLICY;
  const reasons: string[] = [];
  if (removedParts.length > P.maxRemovedParts) reasons.push(`${removedParts.length === 1 ? 'A part the photo shows was' : `${removedParts.length} parts the photo shows were`} left out`);
  if (addedParts.length > P.maxAddedParts) reasons.push(`${addedParts.length} supports were added that the photo does not show`);
  if (maxMoveOfWidth > P.maxMoveOfWidth + 1e-9) reasons.push(`a shelf end or post moved ${Math.round(maxMoveOfWidth * 100)} % of the width`);
  // structure read from the photo and dropped before the repair counts like a part left out
  if (normalised.length) reasons.push(normalised.length === 1 ? normalised[0]!.replace(/\.$/, '') : `${normalised.length} things the photo shows were not rebuilt`);
  const labels = Object.fromEntries([...after.filter((p) => addedParts.includes(p.id)), ...before.filter((p) => removedParts.includes(p.id))].map((p) => [p.id, p.label]));
  return { held: reasons.length > 0, reasons, policy: `${P.id}@${P.version}`, addedParts, removedParts, maxMoveOfWidth: Math.round(maxMoveOfWidth * 1000) / 1000, labels, normalised: [...normalised] };
}

export type FitHistory = History & { hold: RepairHold };

/** A fit history with its hold, validated (a record that does not parse is never trusted). */
const FitHistorySchema = HistorySchema.required({ hold: true });

/** Whether a design carries no fit history, a readable one, or one that cannot be read. */
export function fitHistoryState(params: unknown): { kind: 'none' } | { kind: 'ok'; history: FitHistory } | { kind: 'invalid' } {
  const h = (params as { fitHistory?: unknown } | null)?.fitHistory;
  if (h === undefined || h === null) return { kind: 'none' };
  const p = FitHistorySchema.safeParse(h);
  return p.success ? { kind: 'ok', history: p.data as FitHistory } : { kind: 'invalid' };
}

/** The design's recorded fit history, when a family wrote a readable one. */
export const fitHistoryOf = (params: unknown): FitHistory | null => {
  const s = fitHistoryState(params);
  return s.kind === 'ok' ? s.history : null;
};
